import React, { useState, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Stack,
  Typography,
  Box,
  Collapse,
  useTheme
} from '@rabex-kit/rabex-ui';
import {
  ControlsChevronDownSmall,
  ControlsChevronUpSmall
} from '@heathmont/moon-icons-tw';
import Icon from '../Icon';

// Simple and Clear Data Structure
export interface SubMenu {
  id: string;
  title: string;
  icon?: React.ReactElement<React.SVGProps<SVGSVGElement>>;
  link: string; // Submenus always have links
  openNewTab?: boolean;
}

export interface MenuItem {
  id: string;
  title: string;
  icon: React.ReactElement<React.SVGProps<SVGSVGElement>>;
  link?: string; // Optional link for parent (if no children)
  openNewTab?: boolean;
  submenu?: SubMenu[]; // Option 1: List of submenu items with links
  children?: ReactNode; // Option 2: Any custom React content
  badge?: string | number;
}

interface MenuItemProps {
  item: MenuItem;
}

interface SimpleMobileMenuProps {
  items: MenuItem[];
}

// Submenu Component (renders submenu items with links)
const SubMenuComponent: React.FC<{ submenu: SubMenu }> = ({ submenu }) => {
  const { palette } = useTheme();

  return (
    <NavLink
      to={submenu.link}
      style={{ textDecoration: 'none' }}
      target={submenu.openNewTab ? '_blank' : undefined}
      rel={submenu.openNewTab ? 'noopener noreferrer' : undefined}>
      {({ isActive }) => (
        <Stack
          p={3}
          flexDirection="row"
          justifyContent="space-between"
          sx={{
            cursor: 'pointer',
            backgroundColor: isActive ? palette.base[200] : palette.base[100],
            '&:hover': {
              backgroundColor: isActive ? palette.base[300] : palette.base[200]
            }
          }}>
          <Stack flexDirection="row" gap={1.5} alignItems="center">
            {submenu.icon ? (
              <Icon
                size={24}
                style={{
                  border: `1px dashed ${palette.base[600]}`,
                  borderRadius: '50%'
                }}>
                {submenu.icon}
              </Icon>
            ) : (
              <Box height={24} width={24} />
            )}
            <Typography variant="body2">{submenu.title}</Typography>
          </Stack>
        </Stack>
      )}
    </NavLink>
  );
};

// Main Menu Item Component
const MenuItemComponent: React.FC<MenuItemProps> = ({ item }) => {
  const { palette } = useTheme();
  const [expanded, setExpanded] = useState(false);

  // Check what type of menu item this is
  const hasSubmenu = item.submenu && item.submenu.length > 0;
  const hasChildren = item.children;
  const hasContent = hasSubmenu || hasChildren;

  const handleClick = () => {
    if (hasContent) {
      setExpanded(!expanded);
    }
  };

  // Parent menu styling
  const parentMenuStyle = {
    p: 3,
    cursor: hasContent || item.link ? 'pointer' : 'default',
    borderBottom: `1px solid ${palette.base[200]}`,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: palette.base[100]
    }
  };

  // If parent has a link and no content, make it a simple link
  if (item.link && !hasContent) {
    return (
      <NavLink
        to={item.link}
        style={{ textDecoration: 'none' }}
        target={item.openNewTab ? '_blank' : undefined}
        rel={item.openNewTab ? 'noopener noreferrer' : undefined}>
        {({ isActive }) => (
          <Stack
            p={3}
            flexDirection="row"
            justifyContent="space-between"
            sx={{
              ...parentMenuStyle
              // backgroundColor: isActive
              //   ? alpha(palette.primary.main, 0.12)
              //   : 'transparent'
            }}>
            <Stack flexDirection="row" gap={1.5} alignItems="center">
              {item.icon && <Icon size={24}>{item.icon}</Icon>}
              <Typography variant="body2">{item.title}</Typography>
            </Stack>
            {item.badge && (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                  fontSize: 11,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 0.5
                }}>
                {item.badge}
              </Box>
            )}
          </Stack>
        )}
      </NavLink>
    );
  }

  // Parent menu with content (submenu or children)
  return (
    <Box>
      {/* Parent Menu Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={parentMenuStyle}
        onClick={handleClick}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {item.title}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {item.badge && (
            <Box
              sx={{
                minWidth: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: 'error.main',
                color: 'error.contrastText',
                fontSize: 11,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5
              }}>
              {item.badge}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon size={24}>
              {expanded ? (
                <ControlsChevronUpSmall />
              ) : (
                <ControlsChevronDownSmall />
              )}
            </Icon>
          </Box>
        </Stack>
      </Stack>

      {/* Collapsible Content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            backgroundColor: palette.base[100]
            // borderRadius: 1,
            // m: 1,
            // overflow: 'hidden'
          }}>
          {/* Option 1: Render Submenu (list of links) */}
          {hasSubmenu && (
            <Stack>
              {item.submenu!.map((subMenuItem) => (
                <SubMenuComponent key={subMenuItem.id} submenu={subMenuItem} />
              ))}
            </Stack>
          )}

          {/* Option 2: Render Custom Children (any React content) */}
          {hasChildren && <Box>{item.children}</Box>}
        </Box>
      </Collapse>
    </Box>
  );
};

// Main Menu Component
const MobileMenu: React.FC<SimpleMobileMenuProps> = ({ items }) => {
  return (
    <Stack
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '16px 16px 0 0',
        // maxHeight: '70vh',
        overflow: 'auto'
      }}>
      {/* Menu Items */}
      <Stack>
        {items.map((item) => (
          <MenuItemComponent key={item.id} item={item} />
        ))}
      </Stack>
    </Stack>
  );
};

export default MobileMenu;
